import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Address, HealthcareParty, User} from "@icure/api";
import {MatSort, MatSortable} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {KEYS_PROP, LANGUAGE} from "../../database-user/database-user.component";
import {UserService} from "../../../../../lib/services/user.service";
import {VersionType} from "../../../../../lib/services/access-matrix.service";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UserDialogComponent} from "../dialogs/user-dialog/user-dialog.component";
import {HcpService} from "../../../../../lib/services/hcp.service";
import {LicenseService} from "../../../../shared/license-list/services/license.service";
import {MessageTypeEnum} from "../../database-group/database-group.component";

interface FormatedUserDatas{
  userId: string;
  firstName: string;
  lastName: string;
  name: string;
  parentId: string | null;
  login: string;
  email: string;
  ssin: string;
  nihii: string;
  isKeyGenerate: boolean;
  adr: {
    street: string;
    houseNumber: string;
    city: string;
    country: string;
    postalCode: string;
    region: string;
  },
  speciality: string;
  steps: {
    step_1: boolean;
    step_2: boolean;
    step_3: boolean;
    step_4: boolean;
    step_5: boolean;
  },
  language: string;
  status: string;
  freeStatusHr: string;
  statusHr: string;
}

@Component({
  selector: 'ms-database-group-info-user',
  templateUrl: './database-group-info-user.component.html',
  styleUrls: ['./database-group-info-user.component.scss'],
  standalone: false,
})
export class DatabaseGroupInfoUserComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  @Input() listOfUsers: Array<User> = [];
  @Input() listOfHcps: Array<HealthcareParty> = [];
  @Input() versionType: VersionType = VersionType.FULL;
  @Input() groupInfos: { id?: string } = {};

  public displayedColumns: string[] = [];
  public fullColumns: string[] = [
    'name',
    'login',
    'token',
    'email',
    'nihii',
    'speciality',
    'ssin',
    'street',
    'postalCode',
    'city',
    'country',
    'region',
    'language',
    'statusHr',
  ];
  public freeColumns: string[] = [
    'name',
    'login',
    'token',
    'email',
    'nihii',
    'speciality',
    'ssin',
    'street',
    'postalCode',
    'city',
    'country',
    'region',
    'language',
    'steps',
    'freeStatusHr',
  ];
  public dataSource!: MatTableDataSource<FormatedUserDatas>;
  public formattedUserData: FormatedUserDatas[] = [];
  public inactiveUser: boolean = false;
  public activeUser: boolean = true;
  public selectedFilter: string = '';

  constructor(
    private userService: UserService,
    private hcpService: HcpService,
    public licenseService: LicenseService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  private initialize() {
    this.displayedColumns =
      this.versionType === VersionType.FREE
        ? this.freeColumns
        : this.fullColumns;
    this.sort.sort({ id: 'name', start: 'asc' } as MatSortable);
    this.formattedUserData = this.createDataSource(
      this.listOfUsers,
      this.listOfHcps,
    );
    this.dataSource = new MatTableDataSource(this.formattedUserData);
    this.dataSource.sort = this.sort;
    this.filterChanged();
  }

  private createDataSource(
    listOfUsers: Array<User>,
    listOfHcps: Array<HealthcareParty>,
  ): Array<FormatedUserDatas> {
    const dataSource: Array<FormatedUserDatas> = [];
    listOfUsers?.forEach((user: User) => {
      if (!user?.patientId) {
        const hcp: HealthcareParty | undefined = listOfHcps?.find(
          (candidate) => candidate?.id === user?.healthcarePartyId,
        );
        const hcpAdr: Address | undefined = hcp?.addresses?.find(
          (adr) => adr?.addressType === Address.AddressTypeEnum.Work,
        );
        const hasKeys: boolean =
          !!Object.keys(hcp?.hcPartyKeys ?? {}).length ||
          !!hcp?.publicKeysForOaepWithSha256?.length;
        const name: string =
          hcp?.lastName || hcp?.firstName
            ? `${hcp?.lastName ?? ''} ${hcp?.firstName ?? ''}`.trim()
            : (hcp?.name ?? '');
        const speciality: string = this.userService.getUserSpeciality(
          hcp?.nihii ?? hcp?.options?.['inami'] ?? '',
        );

        dataSource.push({
          userId: user.id || '',
          firstName: hcp?.firstName ?? '',
          lastName: hcp?.lastName ?? '',
          parentId: hcp?.parentId || '',
          name: name,
          login: user.login || '',
          email: user.email || '',
          ssin: hcp?.ssin || '',
          nihii: hcp?.nihii || hcp?.options?.['inami'] || '',
          isKeyGenerate:
            user?.properties?.find(
              (prop) => prop?.type?.identifier === KEYS_PROP,
            )?.typedValue?.booleanValue || false,
          adr: {
            street: hcpAdr?.street || '',
            houseNumber: hcpAdr?.houseNumber || '',
            city: hcpAdr?.city || '',
            country: hcpAdr?.country || '',
            postalCode: hcpAdr?.postalCode || '',
            region:
              hcpAdr?.postalCode && !isNaN(Number(hcpAdr?.postalCode))
                ? this.userService.getDistrictOfPatient(
                    Number(hcpAdr?.postalCode),
                  )
                : '',
          },
          speciality: speciality,
          language:
            user?.properties?.find(
              (prop) => prop?.type?.identifier === LANGUAGE,
            )?.typedValue?.stringValue || '',
          steps: {
            step_1: !!user?.passwordHash,
            step_2: !!hcp?.nihii,
            step_3: !!hcpAdr?.postalCode,
            step_4: !!hcp?.bankAccount,
            step_5: hasKeys || false,
          },
          freeStatusHr: !user?.passwordHash
            ? 'Inactif'
            : hasKeys
              ? 'Actif'
              : !!user?.passwordHash && !hasKeys
                ? 'Bloqué'
                : '',
          status: user?.status ?? '',
          statusHr: user?.status === 'ACTIVE' ? 'Actif' : 'Désactivé',
        });
      }
    });

    return dataSource;
  }

  public filterChanged() {
    let filteredUser: Array<FormatedUserDatas> = [];

    if (this.activeUser) {
      filteredUser = filteredUser.concat(
        this.formattedUserData?.filter(
          (user: FormatedUserDatas) => user?.status === User.StatusEnum.ACTIVE,
        ),
      );
    }
    if (this.inactiveUser) {
      filteredUser = filteredUser.concat(
        this.formattedUserData?.filter(
          (user: FormatedUserDatas) =>
            user?.status === User.StatusEnum.DISABLED,
        ),
      );
    }
    if (!this.activeUser && !this.inactiveUser) {
      filteredUser = filteredUser.concat(this.formattedUserData);
    }

    this.dataSource.data = filteredUser;

    if (this.selectedFilter) {
      const searchFilter = this.selectedFilter;
      this.dataSource.filter = searchFilter;
      if (searchFilter.length > 2) {
        this.dataSource.filter = searchFilter;
      } else {
        this.dataSource.filter = '';
      }
    } else {
      this.dataSource.filter = '';
    }
  }

  public async openUserCreationDialog() {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: { groupInfos: this.groupInfos, listOfHcps: this.listOfHcps },
      height: '700px',
      width: '1000px',
    });

    dialogRef.componentInstance.onCreatedUser.subscribe(
      async (data: { status?: string }) => {
        if (data?.status === 'success') {
          const groupId: string = this.groupInfos?.id ?? '';
          const healthcarePartyIds: string[] = this.listOfUsers
            .filter((user) => !user?.patientId)
            .map((user) => user?.healthcarePartyId ?? '')
            .filter((healthcarePartyId) => healthcarePartyId !== '');
          this.listOfUsers = await this.userService.getUserInGroup(groupId);
          this.listOfHcps = await this.hcpService.getHcpsInGroup(
            groupId,
            healthcarePartyIds,
          );
          this.initialize();
        }
      },
    );
  }

  public async getTemporaryToken(groupId: string, userId: string): Promise<void> {
    if (!groupId || !userId) {
      this.displayWarningMessage(
        'Impossible de générer un token pour cet utilisateur',
        MessageTypeEnum.ERROR,
      );
      return;
    }
    try {
      const token = await this.userService.getTokenForUser(groupId, userId);
      await navigator.clipboard.writeText(token);
      this.displayWarningMessage(
        'Token temporaire copié dans le presse-papier',
        MessageTypeEnum.SUCCESS,
      );
    } catch {
      this.displayWarningMessage(
        'Erreur lors de la génération du token',
        MessageTypeEnum.ERROR,
      );
    }
  }

  private displayWarningMessage(
    message: string,
    messageType: MessageTypeEnum,
  ): void {
    this.snackBar.open(message, '', {
      duration: 5000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: [messageType],
    });
  }
}
